import { Navigate, RouteObject } from 'react-router-dom';
import { RoutePaths } from '@utils/route.utils.ts';
import { uiKitRoutes } from '../ui-kit/ui-kit.routes.tsx';
import UiKitGuard from '@guards/ui-kit.gurad.tsx';
import { settingsRoutes } from '@modules/settings/settings.routes.tsx';
import { entertainmentRoutes } from '@modules/entertainment/entertainment.routes.tsx';
import { castingRoutes } from '@modules/casting/casting.routes.tsx';
import { languagesRoutes } from '@modules/languages/langauges.routes.tsx';
import { infoCenterRoutes } from '@modules/info-center/info-center.routes.tsx';
import { desktopAppRoutes } from '@modules/desktop-app/desktop-app.routes.tsx';
import { digitalSignageRoutes } from '@modules/digital-signage/digital-signage.routes.tsx';
import { profileRoutes } from '@modules/profile/profile.routes.tsx';
import { integrationsRoutes } from '@modules/integrations/integrations.routes.tsx';
import { hotelStaffRoutes } from '@modules/hotel-staff/hotel-staff.routes.tsx';
import { contactUsRoutes } from '@modules/contact-us/contact-us.routes.tsx';
import { departmentsAndUnitsRoutes } from '@modules/departments-and-units/departments-and-units.routes.tsx';
import { hotelManagementRoutes } from '@modules/hotel-management/hotel-management.routes.tsx';
import RoleBasedRedirect from '@core/components/role-based-redirect/role-based-redirect.tsx';
import { promotionRoutes } from '@modules/promotions/promotions-routes.tsx';
import { roomsAssignRoutes } from '@modules/rooms-assign/rooms-assign.routes.tsx';
import { productsRoutes } from '@modules/products/products.routes.tsx';
import { servicesRoutes } from '@modules/services/services.routes.tsx';
import { ordersRoutes } from '@modules/orders/orders.routes.tsx';
import { partnersRoutes } from '@modules/partners/partners.routes.tsx';
import { applicationsRoutes } from '@modules/applications/applications.routes.tsx';
import { releaseRoutes } from '@modules/release/release.routes.tsx';
import { salesRoutes } from '@modules/sales/sales.routes.tsx';
import { catalogRoutes } from '@modules/catalog/catalog.routes.tsx';
import { internalServersConfigurationRoutes } from '@modules/internal-servers-configuration/internal-servers-configuration.routes.tsx';
import { notificationsSettingsRoutes } from '@modules/notifications-settings/notifications-settings.routes.tsx';
import { analyticsRoutes } from '@modules/analytics/analytics.routes.tsx';
import { appBuilderRoutes } from '@modules/app-builder/app-builder.routes.tsx';
import { hotelWifiRoutes } from '@modules/hotel-wifi/hotel-wifi.routes.tsx';
import { hotelWifiSettingsRoutes } from '@modules/hotel-wifi-settings/hotel-wifi-settings.routes.tsx';
import { aiSettingsRoutes } from '@modules/ai-settings/ai-settings.routes.tsx';
import { organizationRoutes } from '@modules/organization/organization.routes.tsx';
import HotelWifiFeatureGuard from '@core/components/hotel-wifi-feature-guard/hotel-wifi-feature-guard.tsx';
import { roomDevicesRoutes } from '@modules/room-devices/room-devices.routes.tsx';

export const coreRoutes: RouteObject[] = [
  {
    path: '/',
    element: <RoleBasedRedirect />
  },
  {
    path: RoutePaths.Core.Partners.path,
    children: [...partnersRoutes]
  },
  {
    path: RoutePaths.Core.Profile.path,
    children: [...profileRoutes]
  },
  {
    path: RoutePaths.Core.Analytics.path,
    children: [...analyticsRoutes]
  },
  {
    path: RoutePaths.Core.RoomsAssign.path,
    children: [...roomsAssignRoutes]
  },
  {
    path: RoutePaths.Core.Entertainment.path,
    children: [...entertainmentRoutes]
  },
  {
    path: RoutePaths.Core.DigitalSignage.path,
    children: [...digitalSignageRoutes]
  },
  {
    path: RoutePaths.Core.RoomDevices.path,
    children: [...roomDevicesRoutes]
  },
  {
    path: RoutePaths.Core.AppBuilder.path,
    children: [...appBuilderRoutes]
  },
  {
    path: RoutePaths.Core.Casting.path,
    children: [...castingRoutes]
  },
  {
    path: RoutePaths.Core.Languages.path,
    children: [...languagesRoutes]
  },
  {
    path: RoutePaths.Core.DesktopApp.path,
    children: [...desktopAppRoutes]
  },
  {
    path: RoutePaths.Core.InternalServersConfiguration.path,
    children: [...internalServersConfigurationRoutes]
  },
  {
    path: RoutePaths.Core.NotificationsSettings.path,
    children: [...notificationsSettingsRoutes]
  },
  {
    path: RoutePaths.Core.InfoCenter.path,
    children: [...infoCenterRoutes]
  },
  {
    path: RoutePaths.Core.Integrations.path,
    children: [...integrationsRoutes]
  },
  {
    path: RoutePaths.Core.Settings.path,
    children: [...settingsRoutes]
  },
  {
    path: RoutePaths.Core.Organization.path,
    children: [...organizationRoutes]
  },
  {
    path: RoutePaths.Core.HotelWifi.path,
    element: (
      <HotelWifiFeatureGuard
        redirectTo={RoutePaths.Core.Analytics.absolutePath}
      />
    ),
    children: [...hotelWifiRoutes]
  },
  {
    path: RoutePaths.Core.HotelWifiSettings.path,
    element: (
      <HotelWifiFeatureGuard
        redirectTo={RoutePaths.Core.Settings.absolutePath}
      />
    ),
    children: [...hotelWifiSettingsRoutes]
  },
  {
    path: RoutePaths.Core.AiSettings.path,
    children: [...aiSettingsRoutes]
  },
  {
    path: RoutePaths.Core.ContactUs.path,
    children: [...contactUsRoutes]
  },
  {
    path: RoutePaths.Core.HotelStaff.path,
    children: [...hotelStaffRoutes]
  },
  {
    path: RoutePaths.Core.Applications.path,
    children: [...applicationsRoutes]
  },
  {
    path: RoutePaths.Core.DepartmentsAndUnits.path,
    children: [...departmentsAndUnitsRoutes]
  },
  {
    path: RoutePaths.Core.HotelManagement.path,
    children: [...hotelManagementRoutes]
  },
  {
    path: RoutePaths.Core.Release.path,
    children: [...releaseRoutes]
  },
  {
    path: RoutePaths.Core.Promotions.path,
    children: [...promotionRoutes]
  },
  {
    path: RoutePaths.Core.Products.path,
    children: [...productsRoutes]
  },
  {
    path: RoutePaths.Core.Services.path,
    children: [...servicesRoutes]
  },
  {
    path: RoutePaths.Core.Orders.path,
    children: [...ordersRoutes]
  },
  {
    path: RoutePaths.Core.Sales.path,
    children: [...salesRoutes]
  },
  {
    path: RoutePaths.Core.Catalog.path,
    children: [...catalogRoutes]
  },
  {
    path: RoutePaths.UiKit.path,
    element: <UiKitGuard />,
    children: [...uiKitRoutes]
  },
  {
    path: '*',
    element: <Navigate to={RoutePaths.Core.Analytics.absolutePath} replace />
  }
];
